import styled from 'styled-components';
import {
  GAPS_TAB,
  GAPS_TAB_TITLE,
  countText,
  vedomostWords,
  volumeTabs,
  type ScreenTab,
  type TabContext,
  type TabStat,
  type VolumeCounters,
  type VolumeTab,
} from './tabs';

const Bar = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const TabList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)};
  padding-bottom: ${({ theme }) => theme.spacing(1)};
`;

const Tab = styled.button<{ $active: boolean }>`
  border: none;
  background: ${({ theme, $active }) => ($active ? theme.colors.primarySoft : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.secondary)};
  font: inherit;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  white-space: nowrap;
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text.primary};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const Count = styled.span`
  margin-left: 6px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  opacity: 0.8;
`;

const Counters = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Counter = styled.span`
  cursor: help;
  white-space: nowrap;

  b {
    color: ${({ theme }) => theme.colors.text.primary};
    font-variant-numeric: tabular-nums;
  }
`;

/** Счётчик-переключатель списка «Есть в ВОР, нет в СП». */
const CounterToggle = styled.button<{ $active: boolean }>`
  border: none;
  background: ${({ theme, $active }) => ($active ? theme.colors.primarySoft : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : 'inherit')};
  font: inherit;
  white-space: nowrap;
  cursor: pointer;
  padding: 0 ${({ theme }) => theme.spacing(1)};
  border-radius: ${({ theme }) => theme.radius.xs};
  text-decoration: underline dotted;
  text-underline-offset: 3px;

  b {
    color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.primary)};
    font-variant-numeric: tabular-nums;
  }
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

interface Props {
  tab: ScreenTab;
  onTab: (t: ScreenTab) => void;
  /** Признаки ревизии: подписи вкладок «Материалы (спецификации на листах)»,
   *  «Объёмы работ (ВР01 и по чертежам)». */
  ctx: TabContext;
  stats: Record<VolumeTab, TabStat>;
  /** null — признаки отбора не загружены: счётчиков нет. */
  counters: VolumeCounters | null;
  /** Открыт список «Есть в ВОР, нет в СП» (вместо вкладки). */
  vorOnly: boolean;
  onVorOnly: (on: boolean) => void;
  /** Вкладка «Анализ комплектности» (№7): анализ включён для проекта и роли. */
  gapsTab: boolean;
}

/** Счётчик «в ведомости»: по правилам выпуска 2 — строки листа «Ведомость
 *  материалов» Excel и позиции экрана в сумме («в ведомости 584 строки (1 332
 *  позиции)»); без них — позиции в сумме, как в выпуске 1б. */
function VedomostCounter({ counters }: { counters: VolumeCounters }) {
  const rows = counters.vedomostRows;
  if (rows === null) {
    return (
      <Counter title="Позиций в перечне ведомости материалов — тот же отбор, что в Excel. Строки ВР01 в перечень не входят (вкладка «Объёмы работ»).">
        в ведомости <b>{counters.inVedomost}</b>
      </Counter>
    );
  }
  const positions = counters.inVedomost;
  const w = vedomostWords(rows, positions);
  return (
    <Counter title="Строки листа «Ведомость материалов» Excel и позиции экрана в сумме: строка ведомости собирает одинаковые позиции разных листов. Строки «на 1 …» — в Excel отдельными строками вне итога, на экране «вне суммы». Строки ВР01 в перечень не входят (вкладка «Объёмы работ»).">
      в ведомости <b>{countText(rows)}</b> {w.rows} (<b>{countText(positions)}</b> {w.positions})
    </Counter>
  );
}

/** Вкладки по части РД и счётчики ведомости: «в ведомости N строк (P позиций)
 *  · повторы на чертежах M · без пары K · есть в ВОР, нет в СП L». Число на
 *  вкладке — показанные строки (без спрятанных повторов). Счётчик «есть в ВОР,
 *  нет в СП» открывает список этих строк с кандидатом пары из СП (выпуск 2).
 *  Последняя вкладка — «Анализ комплектности», если он включён (№7). */
export function VolumesTabs({
  tab,
  onTab,
  ctx,
  stats,
  counters,
  vorOnly,
  onVorOnly,
  gapsTab,
}: Props) {
  return (
    <Bar>
      <TabList
        role="tablist"
        aria-label={gapsTab ? 'Части РД и анализ комплектности' : 'Части РД'}
      >
        {volumeTabs(ctx).map((t) => {
          const s = stats[t.id];
          const rows =
            s.hidden > 0
              ? `Строк: ${s.total}, из них повторов вне суммы ${s.hidden} — спрятаны`
              : `Строк: ${s.total}`;
          const why =
            t.id === 'materials' && ctx.sheetSpecs
              ? 'В комплекте нет СП01/СО: спецификации листов чертежей и позиции ИИ из перечня материалов. '
              : t.id === 'works' && ctx.drawingWorks
                ? 'ВР01 и работы по чертежам: отверстия и проёмы, работы по тексту листов, площадь окраски. '
                : '';
          const hint = why + rows;
          return (
            <Tab
              key={t.id}
              type="button"
              role="tab"
              aria-selected={!vorOnly && tab === t.id}
              $active={!vorOnly && tab === t.id}
              title={hint}
              onClick={() => onTab(t.id)}
            >
              {t.title}
              <Count>{s.shown}</Count>
            </Tab>
          );
        })}
        {gapsTab && (
          <Tab
            type="button"
            role="tab"
            aria-selected={!vorOnly && tab === GAPS_TAB}
            $active={!vorOnly && tab === GAPS_TAB}
            title="Чего, возможно, не хватает в комплекте: по нормам ГЭСН, типовому комплекту, сверке ВОР↔СП и ИИ по кнопке. Решает инженер; в позиции РД и в снабжение не передаётся."
            onClick={() => onTab(GAPS_TAB)}
          >
            {GAPS_TAB_TITLE}
          </Tab>
        )}
      </TabList>
      {counters && (
        <Counters>
          <VedomostCounter counters={counters} />
          {' · '}
          <Counter title="Строки таблиц на чертежах вне суммы: их число уже учтено в другой строке (обычно — в строке СП01); в Excel — лист «Сверка (ЧТЖ и ВР)». По умолчанию спрятаны.">
            повторы на чертежах <b>{counters.drawingRepeats}</b>
          </Counter>
          {counters.unpaired !== null && (
            <>
              {' · '}
              <Counter title="Строки спецификаций на чертежах в перечне без пары в СП01 («нет в СП01 — проверьте») — проверьте, нет ли их в СП01 под другим наименованием или единицей.">
                без пары <b>{counters.unpaired}</b>
              </Counter>
            </>
          )}
          {counters.vorNotInSp !== null && (
            <>
              {' · '}
              {counters.vorNotInSp > 0 ? (
                <CounterToggle
                  type="button"
                  $active={vorOnly}
                  aria-pressed={vorOnly}
                  title={
                    vorOnly
                      ? 'Показан список строк ВОР без пары в спецификации. Нажмите, чтобы вернуться к вкладке.'
                      : 'Строки ВОР, похожие на материалы, без пары в спецификации: в перечень материалов и снабжение не входят — проверьте, не потерялся ли материал. Нажмите — список с кандидатом пары из СП.'
                  }
                  onClick={() => onVorOnly(!vorOnly)}
                >
                  есть в ВОР, нет в СП <b>{counters.vorNotInSp}</b>
                </CounterToggle>
              ) : (
                <Counter title="Строк ВОР, похожих на материалы, без пары в спецификации нет.">
                  есть в ВОР, нет в СП <b>{counters.vorNotInSp}</b>
                </Counter>
              )}
            </>
          )}
        </Counters>
      )}
    </Bar>
  );
}
